import type { IRecentSearch, IRecentSearchItems, TValidatedRecentSearchData } from '@/components/fsf/types'
import { TYPE_AIRPORT } from '@/utils/constants/fsf'
import { RETURN } from '@/utils/constants/fsf'
import { useOrigDestData } from '@/composables/orig-dest/useOrigDestData'
import { COOKIE_OPTIONS } from '@/utils/constants/cookies'
import { usePaxStore } from '@/components/fsf/pax/store'
import { useFsfStore } from '@/components/fsf/stores/fsf.store'

export const useRecentSearch = () => {
  const recentSearch = useCookie<IRecentSearch>('recentSearch', {
    ...COOKIE_OPTIONS
  })

  const MAX_RECENT_SEARCH_ITEMS = 3
  const origDestData = useOrigDestData()
  const paxStore = usePaxStore()
  const fsfStore = useFsfStore()

  const { origin, destin, currentFlightMode, selectedStartDate, selectedEndDate, originType, destinType } =
    storeToRefs(fsfStore)

  const { selected } = storeToRefs(paxStore)

  const cookieExists = computed(() => {
    return Boolean(recentSearch.value && 'items' in recentSearch.value)
  })

  const resetRecentSearch = () => {
    recentSearch.value = {
      items: [],
      language: locale.value
    }
  }

  const getExistingItemAirports = (key: string): { originAirport: string; destinAirport: string } | undefined => {
    if (!cookieExists.value) return undefined

    const cookieLanguage = recentSearch.value.language
    const item = recentSearch.value.items.find((i) => i.key === key)
    if (!item || cookieLanguage !== locale.value) return undefined

    return { originAirport: item.originAirport || '', destinAirport: item.destinAirport || '' }
  }

  const getAirportNames = (origCode: string, destinCode: string): { originAirport: string; destinAirport: string } => {
    const originAirport = `${origDestData.allOrigins.value[origCode + TYPE_AIRPORT]?.city} (${origCode})` || origCode

    const destinAirport =
      `${origDestData.allOriginDestinations(origCode + TYPE_AIRPORT)[destinCode + TYPE_AIRPORT]?.city} (${destinCode})` ||
      destinCode

    return { originAirport, destinAirport }
  }

  const { locale } = useI18n()

  const getRecentSearchData = (validatedData: TValidatedRecentSearchData): IRecentSearchItems => {
    const { originCode, destinCode, tripType, departure, returnDate } = validatedData
    const getOriginType = originType.value || TYPE_AIRPORT
    const getDestinType = destinType.value || TYPE_AIRPORT
    const itemKey = `${originCode}-${destinCode}-${tripType}`

    let originAirport = ''
    let destinAirport = ''

    const existingItemAirports = getExistingItemAirports(itemKey)

    if (!existingItemAirports) {
      const airportNames = getAirportNames(originCode, destinCode)

      originAirport = airportNames.originAirport
      destinAirport = airportNames.destinAirport
    } else {
      originAirport = existingItemAirports.originAirport
      destinAirport = existingItemAirports.destinAirport
    }

    return {
      key: itemKey,
      originCode,
      destinCode,
      originType: getOriginType,
      destinType: getDestinType,
      tripType,
      departure,
      return: returnDate,
      numAdt: selected.value.adults,
      numChd: selected.value.children,
      numInf: selected.value.infants,
      numYth: selected.value.youngAdults,
      originAirport,
      destinAirport
    }
  }

  const validateData = (): TValidatedRecentSearchData | null => {
    const originCode = origin.value
    const destinCode = destin.value
    const tripType = currentFlightMode.value
    const departure = selectedStartDate.value
    let returnDate = selectedEndDate.value

    if (!originCode || !destinCode || !tripType || !departure) return null

    if (tripType === RETURN) {
      if (!returnDate) return null
    } else {
      returnDate = undefined
    }

    return {
      originCode,
      destinCode,
      tripType,
      departure,
      returnDate
    }
  }

  const addToRecentSearch = () => {
    const validatedData = validateData()
    if (!validatedData) return

    const item = getRecentSearchData(validatedData)

    if (!cookieExists.value) {
      resetRecentSearch()
    }

    const list = [...(recentSearch.value?.items || [])]
    const itemIndex = list.findIndex((i) => i.key === item.key)

    if (itemIndex !== -1) {
      list.splice(itemIndex, 1)
    }

    recentSearch.value = {
      items: [item, ...list].slice(0, MAX_RECENT_SEARCH_ITEMS),
      language: locale.value
    }
  }

  const removeFromRecentSearch = (key: string) => {
    recentSearch.value = {
      items: (recentSearch.value?.items ?? []).filter((i) => i.key !== key),
      language: locale.value
    }
  }

  const getRecentSearch = async () => {
    if (!cookieExists.value) {
      resetRecentSearch()
    }

    if (recentSearch.value?.language === locale.value) {
      return recentSearch.value
    }

    await origDestData.fetchOrigDestData(locale.value)

    const translatedItems = (recentSearch.value?.items ?? []).map((item) => {
      return {
        ...item,
        originAirport: getAirportNames(item.originCode, item.destinCode).originAirport ?? '',
        destinAirport: getAirportNames(item.originCode, item.destinCode).destinAirport ?? ''
      }
    })

    recentSearch.value = {
      items: translatedItems,
      language: locale.value
    }

    return recentSearch.value
  }

  return {
    recentSearch,
    addToRecentSearch,
    removeFromRecentSearch,
    getRecentSearch
  }
}
